/**
 * AdminNavItems — leaf + collapsible nav items for AdminLayout sidebar.
 * Extracted for readability and reuse.
 * Memoized so SidebarProvider open/toggle re-renders don't rebuild every row.
 */
import { ChevronDown } from "lucide-react";
import { memo, useEffect, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  SidebarMenuAction,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import type { AdminSidebarNavItem } from "@/components/admin/adminSidebarNav";

/* eslint-disable react-refresh/only-export-components -- useIsItemActive is a tiny hook colocated with the items it serves */
export function useIsItemActive(item: AdminSidebarNavItem): boolean {
  const { pathname } = useLocation();
  if (item.end) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(item.to + "/");
}

// ── NavItemWithChildren ───────────────────────────────────────────────────────
function NavItemWithChildrenInner({
  item,
  onNavigate,
}: {
  item: AdminSidebarNavItem & {
    children: NonNullable<AdminSidebarNavItem["children"]>;
  };
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const isGroupActive =
    location.pathname === item.to || location.pathname.startsWith(item.to + "/");
  const [open, setOpen] = useState(isGroupActive);

  useEffect(() => {
    if (isGroupActive) setOpen(true);
  }, [isGroupActive]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <SidebarMenuItem>
        {/*
          The row navigates to the parent page; only the chevron toggles. When
          these were one button the parent page was unreachable from the
          sidebar — you could open the group but never land on it.
        */}
        <SidebarMenuButton
          asChild
          tooltip={item.label}
          isActive={isGroupActive}
          className={cn(
            // pr-7 keeps the label clear of the absolutely-positioned chevron.
            "h-8 rounded-lg pr-7 text-[13px] transition-[background-color,color,box-shadow,opacity] duration-150 ease-out",
            isGroupActive
              ? "bg-white/10 font-medium text-white ring-1 ring-inset ring-white/[0.08]"
              : "font-normal text-white/55 hover:bg-white/[0.06] hover:text-white/90",
          )}
        >
          {/*
            `end` regardless of item.end: the highlight tracks the whole section
            (isActive above), but aria-current must only fire on the parent page
            itself, never while a child route is open.
          */}
          <NavLink to={item.to} end onClick={onNavigate}>
            <item.icon className="h-4 w-4 shrink-0" aria-hidden />
            <span className="flex-1 truncate">{item.label}</span>
          </NavLink>
        </SidebarMenuButton>

        <CollapsibleTrigger asChild>
          <SidebarMenuAction
            aria-label={`${open ? "Contraer" : "Expandir"} ${item.label}`}
            className="top-1.5 text-white/25 transition-colors duration-150 hover:bg-white/[0.06] hover:text-white/70"
          >
            <motion.div
              animate={{ rotate: open ? 180 : 0 }}
              transition={{ type: "spring", bounce: 0, duration: 0.32 }}
            >
              <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
            </motion.div>
          </SidebarMenuAction>
        </CollapsibleTrigger>

        {/* ── Sub-items ── */}
        <CollapsibleContent>
          <SidebarMenuSub className="ml-[0.8125rem] border-l border-white/[0.07] py-0.5">
            {item.children.map((child) => {
              const hashIdx = child.to.indexOf("#");
              const childPath = hashIdx >= 0 ? child.to.slice(0, hashIdx) : child.to;
              const childHash = hashIdx >= 0 ? "#" + child.to.slice(hashIdx + 1) : "";
              const isChildActive =
                location.pathname === childPath &&
                (childHash ? location.hash === childHash : !location.hash);

              return (
                <SidebarMenuSubItem key={child.to}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={isChildActive}
                    className={cn(
                      "h-7 rounded-md text-[12px] transition-[background-color,color,box-shadow,opacity] duration-150 ease-out",
                      isChildActive
                        ? "bg-white/[0.08] font-medium text-white"
                        : "font-normal text-white/40 hover:bg-white/[0.05] hover:text-white/75",
                    )}
                  >
                    <NavLink to={child.to} onClick={onNavigate}>
                      <child.icon
                        className="h-[0.875rem] w-[0.875rem] shrink-0 opacity-60"
                        aria-hidden
                      />
                      <span className="flex-1 truncate">{child.label}</span>
                    </NavLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

export const NavItemWithChildren = memo(NavItemWithChildrenInner);
NavItemWithChildren.displayName = "NavItemWithChildren";

// ── NavItemLeaf ───────────────────────────────────────────────────────────────
function NavItemLeafInner({
  item,
  onNavigate,
}: {
  item: AdminSidebarNavItem;
  onNavigate?: () => void;
}) {
  const isActive = useIsItemActive(item);
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={item.label}
        isActive={isActive}
        className={cn(
          "h-8 rounded-lg text-[13px] transition-[background-color,color,box-shadow,opacity] duration-150 ease-out",
          isActive
            ? "bg-white/10 font-medium text-white ring-1 ring-inset ring-white/[0.08]"
            : "font-normal text-white/55 hover:bg-white/[0.06] hover:text-white/90",
        )}
      >
        <NavLink to={item.to} end={item.end} onClick={onNavigate}>
          <motion.div
            whileTap={{ scale: 0.97 }}
            transition={{ type: "spring", bounce: 0, duration: 0.28 }}
            className="flex w-full items-center gap-2"
          >
            <item.icon className="h-4 w-4 shrink-0" aria-hidden />
            <span className="flex-1 truncate">{item.label}</span>
            {item.badge != null && item.badge !== "" && (
              <motion.span
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", bounce: 0, duration: 0.28 }}
                className="ml-1.5 rounded bg-amber-400/90 px-1.5 py-px text-[10px] font-medium tabular-nums text-black"
              >
                {item.badge}
              </motion.span>
            )}
          </motion.div>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export const NavItemLeaf = memo(NavItemLeafInner);
NavItemLeaf.displayName = "NavItemLeaf";
